import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLTextAreaElement} [message] - The message field.
 * @property {HTMLElement} [count] - "0/800" above the message.
 * @property {HTMLElement} [sent] - The success card, after a send.
 * @property {HTMLElement} [sentTitle] - "Got it"; `data-named` holds "Got it, __NAME__".
 */

/** sessionStorage key for the sender's name, read back by the success card after the reload. */
const NAME_KEY = 'fifi-contact-name';

/**
 * The V3 contact form (sections/fifi-contact.liquid, design-handoff/design/Info V3.dc.html).
 *
 * - Checks the `[data-validate]` fields (`required` or `email`) on submit, shows each field's
 *   `[data-error]` message (written in Liquid) and focuses the first problem. Fixing a field
 *   clears its message.
 * - Keeps the message counter up to date (`on:input="/updateCount"`).
 * - After a send, Shopify reloads the page with the success card. The name saved on submit fills
 *   "Got it, __NAME__"; without it the card keeps "Got it".
 *
 * @extends {Component<Refs>}
 */
class FifiContactForm extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const form = this.querySelector('form');
    if (form) {
      form.noValidate = true;
      form.addEventListener('submit', this.#onSubmit, { signal: this.#abort.signal });
      form.addEventListener('input', this.#onInput, { signal: this.#abort.signal });
    }

    this.updateCount();
    this.#showSent();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  updateCount() {
    const { message, count } = this.refs;
    if (!message || !count) return;

    const length = message.value.length;
    count.textContent = `${length}/${message.maxLength}`;
    count.toggleAttribute('data-full', length >= message.maxLength);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    /** @type {HTMLInputElement | HTMLTextAreaElement | null} */
    let firstInvalid = null;

    for (const field of form.querySelectorAll('[data-validate]')) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;

      const valid = this.#isValid(field);
      this.#setError(field, !valid);
      if (!valid && !firstInvalid) firstInvalid = field;
    }

    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
      return;
    }

    const name = form.querySelector('[name="contact[name]"]');
    if (name instanceof HTMLInputElement) {
      try {
        sessionStorage.setItem(NAME_KEY, name.value.trim());
      } catch {
        // Storage can be blocked; the success card then says "Got it".
      }
    }
  };

  /** Clears a field's message once it's fixed. @param {Event} event */
  #onInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    if (field.getAttribute('aria-invalid') !== 'true' || !this.#isValid(field)) return;

    this.#setError(field, false);
  };

  /** @param {HTMLInputElement | HTMLTextAreaElement} field */
  #isValid(field) {
    const value = field.value.trim();
    if (field.dataset.validate === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    return value !== '';
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {boolean} invalid
   */
  #setError(field, invalid) {
    if (invalid) {
      field.setAttribute('aria-invalid', 'true');
    } else {
      field.removeAttribute('aria-invalid');
    }

    const error = field.closest('.fifi-contact__field')?.querySelector('[data-error]');
    if (error instanceof HTMLElement) error.hidden = !invalid;
  }

  #showSent() {
    const { sent, sentTitle } = this.refs;
    if (!sent) return;

    let name = '';
    try {
      name = sessionStorage.getItem(NAME_KEY) ?? '';
      sessionStorage.removeItem(NAME_KEY);
    } catch {
      // Keep "Got it".
    }

    const named = sentTitle?.dataset.named;
    if (sentTitle && named && name) sentTitle.textContent = named.replace('__NAME__', name);

    sent.focus();
  }
}

if (!customElements.get('fifi-contact-form')) {
  customElements.define('fifi-contact-form', FifiContactForm);
}
